import axios from 'axios';
import { notification } from 'antd';
import Cookies from 'js-cookie';
import {
  applyTokensFromRefreshResponse,
  clearAuthSessionCookies,
  getRefreshToken,
  isAuthRefreshResponseSuccess,
  isRefreshSessionExpired,
} from '../authSession';
import { getItem } from '../../utility/cookieStorage';

const API_ENDPOINT = `${process.env.REACT_APP_API_ENDPOINT}/api`;

/** Interceptor yok: refresh yanıtı ana client zincirine girmez (401 döngüsünü keser). */
const refreshClient = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/** Paralel 401'lerde tek refresh. */
let refreshInFlight = null;

let isLoggingOut = false;

const MAX_REQUEST_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 800;
const RETRYABLE_HTTP_STATUSES = new Set([408, 502, 503, 504]);

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isTransientRequestError(error) {
  const { response, config } = error;
  if (!config || config.skipRetry) {
    return false;
  }
  if (!response) {
    return true;
  }
  const method = (config.method || 'get').toLowerCase();
  return method === 'get' && RETRYABLE_HTTP_STATUSES.has(response.status);
}

const authHeader = () => ({
  Authorization: `Bearer ${getItem('access_token')}`,
});

const client = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    Authorization: `Bearer ${getItem('access_token')}`,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

async function retryRequestIfNeeded(error) {
  const { config } = error;
  if (!isTransientRequestError(error)) {
    return null;
  }

  const retryCount = config.retryCount || 0;
  if (retryCount >= MAX_REQUEST_RETRIES) {
    return null;
  }

  config.retryCount = retryCount + 1;
  const delayMs = RETRY_BASE_DELAY_MS * 2 ** (config.retryCount - 1);
  await sleep(delayMs);
  return client(config);
}

/**
 * POST /auth/refresh — Authorization yok; body: { refresh_token }.
 */
export async function postAuthRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    const err = new Error('No refresh token');
    err.code = 'NO_REFRESH';
    throw err;
  }
  return refreshClient.post('/v1/auth/refresh', { refresh_token: refreshToken });
}

export function resetRefreshFailureStreak() {
  // Geriye dönük uyumluluk; refresh hatasında artık anında çıkış yapılır.
}

async function clearSessionAndReload() {
  if (isLoggingOut) {
    return;
  }
  isLoggingOut = true;

  clearAuthSessionCookies();

  try {
    // eslint-disable-next-line import/no-cycle
    const PermissionServiceModule = await import('../../services/permissionService');
    PermissionServiceModule.default.clearPermissions();
  } catch (permissionClearError) {
    // Ignore permission clearing errors
  }

  window.location.reload();
}

/**
 * Refresh başarısızlığında eski token'ları hemen temizler ve oturumu kapatır.
 * @returns {Promise<boolean>} her zaman true (çıkış tetiklendi)
 */
export async function registerRefreshFailureAndLogoutIfNeeded() {
  await clearSessionAndReload();
  return true;
}

function applyRefreshResponse(responseData) {
  applyTokensFromRefreshResponse(responseData);
}

/**
 * API isteği 401 sonrası: opaque refresh ile access yenile, başarıda sayfayı yenile.
 */
async function recoverFrom401WithReload() {
  try {
    const refresh = await postAuthRefresh();
    if (isAuthRefreshResponseSuccess(refresh.data)) {
      applyRefreshResponse(refresh.data);

      try {
        const currentOrgId = Cookies.get('currentOrganizationId');
        if (currentOrgId) {
          // eslint-disable-next-line import/no-cycle
          const PermissionServiceModule = await import('../../services/permissionService');
          await PermissionServiceModule.default.loadPermissionsForOrganization(currentOrgId);
        }
      } catch (permissionError) {
        // eslint-disable-next-line no-console
        console.warn('Failed to refresh permissions after token refresh:', permissionError);
      }

      window.location.reload();
      return;
    }

    await registerRefreshFailureAndLogoutIfNeeded();
  } catch (errorx) {
    await registerRefreshFailureAndLogoutIfNeeded();
  }
}

/**
 * Periyodik oturum yenileme; sayfa yenilenmez (sessionRefreshService).
 */
export async function performSilentSessionRefresh() {
  if (isRefreshSessionExpired()) {
    await clearSessionAndReload();
    return { ok: false, loggedOut: true };
  }

  try {
    const response = await postAuthRefresh();
    if (isAuthRefreshResponseSuccess(response.data)) {
      applyRefreshResponse(response.data);

      try {
        const currentOrgId = Cookies.get('currentOrganizationId');
        if (currentOrgId) {
          const PermissionServiceModule = await import('../../services/permissionService');
          await PermissionServiceModule.default.loadPermissionsForOrganization(currentOrgId);
        }
      } catch (permissionError) {
        // eslint-disable-next-line no-console
        console.warn('Session refresh: permission reload failed', permissionError);
      }
      return { ok: true };
    }

    const loggedOut = await registerRefreshFailureAndLogoutIfNeeded();
    return { ok: false, loggedOut };
  } catch (err) {
    const loggedOut = await registerRefreshFailureAndLogoutIfNeeded();
    return { ok: false, loggedOut, error: err };
  }
}

class DataService {
  static get(path = '') {
    return client({
      method: 'GET',
      url: path,
      headers: { ...authHeader() },
    });
  }

  static post(path = '', data = {}, optionalHeader = {}) {
    return client({
      method: 'POST',
      url: path,
      data,
      headers: { ...authHeader(), ...optionalHeader },
    });
  }

  static patch(path = '', data = {}) {
    return client({
      method: 'PATCH',
      url: path,
      data: JSON.stringify(data),
      headers: { ...authHeader() },
    });
  }

  static put(path = '', data = {}) {
    return client({
      method: 'PUT',
      url: path,
      data: JSON.stringify(data),
      headers: { ...authHeader() },
    });
  }

  static delete(path = '', data = {}) {
    return client({
      method: 'DELETE',
      url: path,
      data: JSON.stringify(data),
      headers: { ...authHeader() },
    });
  }
}

/**
 * axios interceptors runs before and after a request, letting the developer modify req,req more
 * For more details on axios interceptor see https://github.com/axios/axios#interceptors
 */
client.interceptors.request.use((config) => {
  // do something before executing the request
  // For example tag along the bearer access token to request header or set a cookie
  const requestConfig = config;
  const { headers } = config;
  requestConfig.headers = { ...headers, Authorization: `Bearer ${getItem('access_token')}` };

  return requestConfig;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const retriedResponse = await retryRequestIfNeeded(error);
    if (retriedResponse) {
      return retriedResponse;
    }

    const { response } = error;
    if (response) {
      if (response.status === 401) {
        if (isLoggingOut) {
          return Promise.reject(error);
        }

        const hasRefresh = !!getRefreshToken();
        if (!hasRefresh || isRefreshSessionExpired()) {
          if (!refreshInFlight) {
            refreshInFlight = clearSessionAndReload().finally(() => {
              refreshInFlight = null;
            });
          }
          await refreshInFlight;

          notification.error({
            message: 'Error',
            description: response.data?.message,
          });
          return Promise.reject(error);
        }

        if (!refreshInFlight) {
          refreshInFlight = recoverFrom401WithReload().finally(() => {
            refreshInFlight = null;
          });
        }
        await refreshInFlight;

        notification.error({
          message: 'Error',
          description: response.data?.message,
        });
      }
      if (response.status === 500) {
        // console.log(response);
      }
    }
    return Promise.reject(error);
  },
);
export { DataService };
